import { useEffect, useRef } from 'react';
import SubtitleMQTTClient from '@/utils/mqtt';
import { getShareConnect } from '@/api/subtitles';

interface IProps {
  roomId?: string;
  onMessage: (msg: API.TranslateList) => void;
  onLoaded?: () => void;
  /** MQTT 断线后重连成功时回调(首次连接不触发)。用于补拉断线窗口丢失的字幕 */
  onReconnected?: () => void;
}

function useShareMqtt({ roomId, onMessage, onLoaded, onReconnected }: IProps) {
  const isDestroyed = useRef(false);
  const clientRef = useRef<SubtitleMQTTClient<API.TranslateList>>();
  const lastFocusTime = useRef<number>(Date.now());
  // 是否已成功连接过:用于区分「首次连接」与「断线重连」
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (!roomId) {
      return;
    }
    // roomId 变化(固定链接跨场切换)会重跑本 effect:必须复位销毁标志,
    // 否则上一场 cleanup 置下的 true 会让新场的 initClient 直接 return,MQTT 永远不重连
    isDestroyed.current = false;

    const onFocus = () => {
      const now = Date.now();

      if (now - 5000 > lastFocusTime.current) {
        lastFocusTime.current = now;

        if (!clientRef.current) {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          initClient();
        }
      }
    };

    window.addEventListener('focus', onFocus);

    const getMqttInfo = () => getShareConnect(roomId).then((res) => {
      console.log('>> getShareConnect', res);
      if (res.data.status !== 200) {
        throw new Error('getMqttInfo Failed');
      }

      return res.data.data;
    });

    const initClient = () => {
      getMqttInfo().then((res) => {
        if (!res.status || isDestroyed.current) {
          return;
        }

        console.log('MQTT Connecting');

        clientRef.current = new SubtitleMQTTClient<API.TranslateList>({
          roomId,
          info: res,
          getToken: () => getMqttInfo(),
          onMessage: (msg) => {
            console.log('>> onMessage', msg);
            // 全部语言放行,不在这里按当前语言丢弃。
            // 原因:desktop 把所有 desktop_langs 译文实时广播给「所有」观众。若按
            // selectedLang 丢弃,观众看 A 语言时 B 语言译文被扔掉;之后切到 B,旧句的
            // B 译文已丢、desktop 又不会重推旧句 → 旧句永久卡"翻译中"。
            // 改为:前端把所有语言译文都缓存下来(index.tsx translationsRef 按 lang 存),
            // 切语言时直接从缓存取,旧句立刻有译文。
            onMessage(msg);
          },
          topics: [res.share_translate_topic],
          onLoaded: () => {
            onLoaded?.();
          },
          // clean session 下断线窗口的消息 broker 不保留,重连成功后由页面补拉
          // 历史字幕填缺口(getLogDetail + mergeRealtimeAndHistory 天然去重)
          onStatusChange: (status) => {
            if (status) {
              if (hasConnectedRef.current) {
                onReconnected?.();
              }
              hasConnectedRef.current = true;
            }
          },
        });
      });
    };

    initClient();

    // eslint-disable-next-line consistent-return
    return () => {
      isDestroyed.current = true;

      if (clientRef.current) {
        clientRef.current.destroy();
        // 释放引用:跨场重连时 initClient/onFocus 依赖 clientRef 判空
        clientRef.current = undefined;
      }

      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);
}

export default useShareMqtt;
